/*
 * ArticleEntertainment.tsx — /entertainment-brasov-guide
 *
 * Design Philosophy: Editorial Minimalism with Architectural Precision
 * - Same visual language as existing articles: Playfair Display headings, Source Sans 3 body
 * - Muted earth tones, generous whitespace, data tables for price reference
 * - Hero: Strada Republicii at dusk; in-article: Parc Aventura zipline
 *
 * PRICE NOTE: Prices reflect current market rates as of April 2026.
 */

import { useState } from "react";
import { Clock, Share2, ChevronRight, MapPin, ExternalLink, Sun, Moon, Check } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { Link } from "wouter";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { subscribeToNewsletter } from "@/lib/brevo";

const HERO_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/article5-piata-sfatului-night_37c559b4.webp";
const DAY_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/article5-entertainment-day-NeneVLRHo3xeSxtSNGWVeD.webp";
const REALESTATE_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/hero-real-estate-oVkqv2kDQCBzogKNZUAGJw.webp";

function NewsletterInlineLight() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle"|"loading"|"success"|"error">("idle");
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setStatus("loading");
    try {
      await subscribeToNewsletter(email);
      setStatus("success");
    } catch { setStatus("error"); }
  };
  if (status === "success") return <p className="text-xs text-green-700 py-2">✓ Subscribed! See you Friday.</p>;
  return (
    <form onSubmit={handleSubmit}>
      <input type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="your@email.com" className="w-full border border-stone-200 px-3 py-2 text-xs text-stone-700 outline-none focus:border-stone-400 mb-2" />
      <button type="submit" disabled={status === "loading"} className="w-full py-2 text-xs text-white font-semibold disabled:opacity-60" style={{ backgroundColor: "oklch(0.38 0.12 15)", letterSpacing: "0.06em" }}>
        {status === "loading" ? "..." : "SUBSCRIBE"}
      </button>
      {status === "error" && <p className="text-xs text-red-500 mt-1">Try again.</p>}
    </form>
  );
}

function SidebarAd() {
  return (
    <div className="border border-stone-200 overflow-hidden">
      <div className="px-3 py-1.5" style={{ backgroundColor: "oklch(0.95 0.008 75)" }}>
        <span className="text-xs text-stone-400 uppercase tracking-widest">Partner Content · Cartier Transilvania</span>
      </div>
      <div className="relative">
        <img src="https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/XbbLvxSueOHmjHcF.webp" alt="Cartier Transilvania Brașov" className="w-full h-48 object-cover" loading="lazy" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to top, oklch(0.12 0.01 60 / 0.8) 0%, transparent 60%)" }} />
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <p className="text-white text-xs uppercase tracking-widest mb-1 font-semibold">New development · Noua</p>
          <h4 className="text-white font-bold leading-tight" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.1rem" }}>
            Cartier Transilvania
          </h4>
        </div>
      </div>
      <div className="p-4">
        <p className="text-stone-600 text-sm font-light leading-relaxed mb-3">
          208 apartments in 6 blocks. T1–T3 completed &amp; occupied. T4–T6 delivering September 2027. Next to Lake Noua.
        </p>
        <div className="flex items-center gap-1 text-xs text-stone-500 mb-4">
          <MapPin size={11} />
          <span>Noua, Brașov</span>
        </div>
        <a
          href="https://www.cartiertransilvania.ro/en/"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-1 text-xs text-white w-full px-3 py-2 transition-opacity hover:opacity-90"
          style={{ backgroundColor: "oklch(0.38 0.12 15)", letterSpacing: "0.06em" }}>
          VIEW PROJECT <ExternalLink size={10} />
        </a>
      </div>
    </div>
  );
}

function InArticleBanner() {
  return (
    <div className="my-10 border border-stone-200 overflow-hidden">
      <div className="px-4 py-1.5" style={{ backgroundColor: "oklch(0.95 0.008 75)" }}>
        <span className="text-xs text-stone-400 uppercase tracking-widest">Partner content · Cartier Transilvania</span>
      </div>
      <div className="flex flex-col sm:flex-row">
        <img src="https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/XbbLvxSueOHmjHcF.webp" alt="Cartier Transilvania" className="w-full sm:w-48 h-36 object-cover flex-shrink-0" loading="lazy" />
        <div className="p-5 flex flex-col justify-between">
          <div>
            <p className="text-xs uppercase tracking-widest font-semibold mb-2" style={{ color: "oklch(0.38 0.12 15)" }}>
              Apartments for sale in Brașov
            </p>
            <h4 className="font-bold text-stone-900 mb-2 leading-snug" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.05rem" }}>
              Cartier Transilvania — 208 apartments in Noua
            </h4>
            <p className="text-stone-500 text-sm font-light leading-relaxed">
              T1, T2, T3 completed and occupied. T4, T5, T6 under construction — delivery September 2027. Solar panels &amp; heat pump heating.
            </p>
          </div>
          <div className="flex items-center gap-3 mt-4">
            <a
              href="https://www.cartiertransilvania.ro/en/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-xs text-white px-4 py-2 transition-opacity hover:opacity-90"
              style={{ backgroundColor: "oklch(0.38 0.12 15)", letterSpacing: "0.06em" }}>
              VISIT PROJECT SITE <ChevronRight size={11} />
            </a>
            <span className="text-xs text-stone-400">cartiertransilvania.ro</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function PriceTable({ rows }: { rows: { item: string; price: string; notes?: string }[] }) {
  return (
    <div className="my-6 overflow-x-auto">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr style={{ backgroundColor: "oklch(0.95 0.008 75)" }}>
            <th className="text-left px-4 py-3 text-stone-600 font-semibold text-xs uppercase tracking-wider border-b border-stone-200">Activity / Item</th>
            <th className="text-right px-4 py-3 text-stone-600 font-semibold text-xs uppercase tracking-wider border-b border-stone-200">Price (RON)</th>
            <th className="text-left px-4 py-3 text-stone-600 font-semibold text-xs uppercase tracking-wider border-b border-stone-200">Notes</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} style={{ backgroundColor: i % 2 !== 0 ? "oklch(0.985 0.006 75)" : "white" }}>
              <td className="px-4 py-3 text-stone-700 border-b border-stone-100">{row.item}</td>
              <td className="px-4 py-3 text-right text-stone-800 border-b border-stone-100 font-mono text-xs font-semibold">{row.price}</td>
              <td className="px-4 py-3 text-stone-500 border-b border-stone-100 text-xs font-light">{row.notes || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SectionLabel({ icon, label, color }: { icon: React.ReactNode; label: string; color: string }) {
  return (
    <div className="flex items-center gap-3 mt-12 mb-5">
      <div className="flex items-center justify-center w-8 h-8 text-white" style={{ backgroundColor: color }}>
        {icon}
      </div>
      <h2 className="text-stone-900" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.5rem", fontWeight: 700 }}>
        {label}
      </h2>
    </div>
  );
}

function ShareButton({ url, title }: { url: string; title: string }) {
  const [copied, setCopied] = useState(false);
  const handleShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title, url }); } catch { /* cancelled */ }
    } else {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };
  return (
    <button onClick={handleShare} className="flex items-center gap-2 text-xs text-stone-500 hover:text-stone-800 transition-colors">
      {copied ? <Check size={13} className="text-green-600" /> : <Share2 size={13} />}
      {copied ? "Copied!" : "Share"}
    </button>
  );
}

export default function ArticleEntertainment() {
  return (
    <>
    <Helmet>
      <title>Things to Do in Brașov: Entertainment Day & Night Guide for Adults</title>
      <meta name="description" content="Things to do in Brașov: bars, clubs, live music, outdoor activities and more. A practical guide for expats and visitors with real prices. Updated 2026." />
      <meta name="keywords" content="things to do in Brasov, entertainment Brasov, Brasov nightlife, Brasov activities, what to do in Brasov Romania" />
      <meta property="og:title" content="Things to Do in Brașov: Entertainment Day & Night Guide for Adults" />
      <meta property="og:description" content="Things to do in Brașov: bars, clubs, live music, outdoor activities and more. A practical guide for expats and visitors with real prices. Updated 2026." />
      <meta property="og:image" content="https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/article5-piata-sfatului-night_37c559b4.webp" />
      <meta property="og:locale" content="en_US" />
      <meta property="og:type" content="article" />
      <meta property="og:url" content="https://brasovgateway.com/entertainment-brasov-guide" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Things to Do in Brașov: Entertainment Day & Night Guide for Adults" />
      <link rel="canonical" href="https://brasovgateway.com/entertainment-brasov-guide" />
      <script type="application/ld+json">{`
        {
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Article",
              "@id": "https://brasovgateway.com/entertainment-brasov-guide#article",
              "headline": "Entertainment in Brașov: Day & Night for Adults — with Real Prices",
              "description": "The best bars, restaurants, outdoor activities, and nightlife in Brașov — a practical guide for expats and visitors with real prices.",
              "image": "https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/article5-piata-sfatului-night_37c559b4.webp",
              "datePublished": "2026-04-03",
              "dateModified": "2026-04-03",
              "author": {
                "@type": "Organization",
                "name": "Brașov Gateway",
                "url": "https://brasovgateway.com"
              },
              "publisher": {
                "@type": "Organization",
                "name": "Brașov Gateway",
                "url": "https://brasovgateway.com"
              },
              "url": "https://brasovgateway.com/entertainment-brasov-guide",
              "inLanguage": "en",
              "articleSection": "Lifestyle"
            },
            {
              "@type": "BreadcrumbList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "name": "Home",
                  "item": "https://brasovgateway.com"
                },
                {
                  "@type": "ListItem",
                  "position": 2,
                  "name": "Blog",
                  "item": "https://brasovgateway.com/blog"
                },
                {
                  "@type": "ListItem",
                  "position": 3,
                  "name": "Entertainment in Brașov: Day & Night for Adults — with Real ...",
                  "item": "https://brasovgateway.com/entertainment-brasov-guide"
                }
              ]
            }
          ]
        }
      `}</script>
    </Helmet>
    <div className="min-h-screen" style={{ backgroundColor: "oklch(0.99 0.004 75)", fontFamily: "Source Sans 3, sans-serif" }}>
      <SiteHeader />

      {/* Hero */}
      <div className="relative w-full" style={{ height: "clamp(280px, 45vw, 520px)" }}>
        <img src={HERO_IMG} alt="Things to do in Brașov — Piața Sfătului by night" className="w-full h-full object-cover" fetchPriority="high" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, transparent 30%, oklch(0.08 0.01 60 / 0.75) 100%)" }} />
        <div className="absolute bottom-0 left-0 right-0 container pb-8">
          <span className="inline-block text-xs uppercase tracking-widest font-semibold px-2 py-1 mb-3 text-white" style={{ backgroundColor: "oklch(0.38 0.12 15)" }}>
            Lifestyle
          </span>
          <h1 className="text-white leading-tight" style={{ fontFamily: "Playfair Display, serif", fontSize: "clamp(1.6rem, 4vw, 2.6rem)", fontWeight: 700, textShadow: "0 2px 12px rgba(0,0,0,0.4)" }}>
            Things to do in Brașov: entertainment, activities and nightlife — with real prices
          </h1>
        </div>
      </div>

      <div className="container py-8">
        <div className="flex flex-col lg:flex-row gap-12">
          {/* Main content */}
          <main className="flex-1 min-w-0">
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex items-center gap-1 text-stone-400 text-xs">
                  <Clock size={11} />
                  <span>9 min read</span>
                </div>
              </div>
              <div className="flex items-center justify-between py-4 border-t border-b border-stone-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full overflow-hidden" style={{ backgroundColor: "oklch(0.95 0.008 75)" }}>
                    <img src="https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/XbbLvxSueOHmjHcF.webp" alt="Brașov Gateway" className="w-full h-full object-cover opacity-50" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-stone-900">Brașov Gateway Editorial</p>
                    <p className="text-xs text-stone-500">Travel &amp; Lifestyle Team</p>
                  </div>
                </div>
                <ShareButton url="https://brasovgateway.com/entertainment-brasov-guide" title="Entertainment in Brașov: Day & Night for Adults — with Real Prices" />
              </div>
            </div>

            {/* Article body */}
            <div>
              <p className="text-stone-700 text-lg font-light leading-relaxed mb-6" style={{ lineHeight: "1.8" }}>
                Looking for things to do in Brașov? The city has a reputation as a cultural and outdoor hub — but what does that actually mean for someone who lives here or plans to stay for a while? (If you're still deciding whether to move here at all, start with <Link href="/why-expats-choose-brasov" className="underline decoration-dotted underline-offset-2 hover:text-stone-900 transition-colors" style={{ color: "oklch(0.38 0.12 15)" }}>our honest guide for expats</Link>.) This article maps out the full entertainment landscape: from morning hikes and afternoon spa sessions to craft beer bars and rooftop clubs at midnight. All prices reflect current market rates as of April 2026.
              </p>


              {/* Exchange rate note */}
              <div className="my-6 px-5 py-4 border-l-4" style={{ borderLeftColor: "oklch(0.38 0.12 15)", backgroundColor: "oklch(0.97 0.005 75)" }}>
                <p className="text-stone-600 text-sm font-light leading-relaxed">
                  <strong className="text-stone-800">Currency note:</strong> Prices are given in Romanian leu (RON). We use an approximate rate of <strong>5 RON = 1 EUR</strong>. A beer that costs 16 RON is roughly €3.20; a cocktail at 36 RON is about €7.20.
                </p>
              </div>

              {/* ===== DAYTIME ===== */}
              <SectionLabel icon={<Sun size={16} />} label="Daytime: what to do before the sun goes down" color="oklch(0.55 0.14 60)" />

              <h3 id="telecabina-tampa" className="text-stone-800 mt-6 mb-3" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.15rem", fontWeight: 700 }}>
                Telecabina Tâmpa — the quickest way to a panoramic view
              </h3>
              <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                The cable car up Mount Tâmpa is one of the most satisfying things you can do in Brașov for under €12. The ride takes about four minutes and deposits you at 960 metres, with a sweeping view of the city, the Carpathian ridgeline, and — on clear days — all the way to the Bucegi plateau. The renovated upper station now houses a panoramic restaurant, making it a solid option for a late afternoon drink with a view. Operating hours are 10:00–23:00 (Tuesday–Sunday, closed Monday).
              </p>
              <PriceTable rows={[
                { item: "Cable car — return ticket (adult)", price: "45 RON (~€9)", notes: "Tuesday–Sunday; closed Monday" },
                { item: "Cable car — one-way up (adult)", price: "30 RON (~€6)", notes: "If you prefer to hike down" },
                { item: "Cable car — return ticket (child)", price: "30 RON (~€6)", notes: "Under 12 years" },
              ]} />

              <h3 id="escape-rooms" className="text-stone-800 mt-8 mb-3" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.15rem", fontWeight: 700 }}>
                Escape rooms — Brașov's best indoor activity
              </h3>
              <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                Brașov has a surprisingly strong escape room scene. <strong>Obscuria</strong> (Str. Castanilor 4) is consistently rated among the best in Romania, with three rooms themed around Dracula, Cold War espionage, and a subway bomb scenario. <strong>Puzzle Punks</strong> (Str. Republicii) combines escape rooms with a board game café — a good option if your group wants to extend the evening. Prices are per group, not per person, making them excellent value for 3–5 people.
              </p>
              <PriceTable rows={[
                { item: "Escape room — 2 people (Puzzle Punks)", price: "220 RON (~€44)", notes: "Per group" },
                { item: "Escape room — 3 people (Puzzle Punks)", price: "270 RON (~€54)", notes: "Per group" },
                { item: "Escape room — 4 people (Puzzle Punks)", price: "320 RON (~€64)", notes: "Per group" },
                { item: "Escape room — 5 people (Puzzle Punks)", price: "400 RON (~€80)", notes: "Per group" },
                { item: "Obscuria rooms (2–6 people)", price: "200–360 RON", notes: "Varies by room; book online" },
              ]} />

              <h3 id="parc-aventura" className="text-stone-800 mt-8 mb-3" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.15rem", fontWeight: 700 }}>
                Parc Aventura — treetop adventure for adults
              </h3>
              <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                Set in the forest near Lacul Noua, Parc Aventura is a serious outdoor adventure park — not a children's playground. The adult circuit includes ziplines, rope bridges, and climbing challenges at varying heights, with a full session running two hours. It is genuinely physical and genuinely fun, and the forest setting makes it one of the most scenic ways to spend a morning in Brașov. Booking in advance is recommended for weekends.
              </p>
              <PriceTable rows={[
                { item: "Adult circuit (2 hours, 12+ years)", price: "125 RON (~€25)", notes: "Book in advance for weekends" },
                { item: "Extra hour (adult)", price: "50 RON (~€10)", notes: "Per additional hour" },
                { item: "Children's circuit (4–11 years)", price: "115 RON (~€23)", notes: "Supervised, shorter circuit" },
              ]} />

              {/* In-article image */}
              <div className="my-10">
                <img src={DAY_IMG} alt="Parc Aventura zipline Brașov" className="w-full object-cover" style={{ maxHeight: "420px" }} loading="lazy" />
                <p className="text-xs text-stone-400 mt-2 font-light">Parc Aventura Brașov — adult zipline circuit in the forest near Lacul Noua</p>
              </div>

              <h3 id="paradisul-acvatic" className="text-stone-800 mt-8 mb-3" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.15rem", fontWeight: 700 }}>
                Paradisul Acvatic — the city's main water complex
              </h3>
              <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                Located on Bulevardul Grivitei, Paradisul Acvatic is Brașov's largest aquatic complex, with seven pools including an Olympic-size pool, waterslides, a jacuzzi, and a sauna area. It is a legitimate full-day destination — not a tourist trap. Adults use it for lap swimming, families for the slides, and couples for the spa area. The complex is open Tuesday–Sunday; Monday is closed for maintenance.
              </p>
              <PriceTable rows={[
                { item: "2-hour access (adult)", price: "70 RON (~€14)", notes: "Tuesday–Sunday; closed Monday" },
                { item: "3-hour access (adult)", price: "85 RON (~€17)", notes: "—" },
                { item: "Full-day access (adult)", price: "110 RON (~€22)", notes: "Best value option" },
                { item: "Child (under 1.40 m height)", price: "45 RON (~€9)", notes: "Unlimited access" },
              ]} />

              <h3 id="bowling-billiards" className="text-stone-800 mt-8 mb-3" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.15rem", fontWeight: 700 }}>
                Bowling & billiards — low-key afternoons
              </h3>
              <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                For a relaxed afternoon with friends, Brașov has two main options. <strong>Dash</strong> (Bulevardul Gării 7) is the more local, no-frills choice — good lanes, fair prices, and a bar. <strong>TrickShot</strong> at AFI Brașov mall is the larger, more modern venue, with bowling, billiards, and arcade games under one roof. Prices are significantly lower on weekday mornings.
              </p>
              <PriceTable rows={[
                { item: "Bowling — Dash (per person/game)", price: "25 RON (~€5)", notes: "All week" },
                { item: "Billiards — Dash (per hour)", price: "40 RON (~€8)", notes: "—" },
                { item: "Bowling — TrickShot (weekday morning, per lane/hour)", price: "55 RON (~€11)", notes: "Mon–Fri 10:00–13:00" },
                { item: "Bowling — TrickShot (weekday afternoon/evening)", price: "98 RON (~€19.60)", notes: "Per lane/hour" },
                { item: "Bowling — TrickShot (weekend)", price: "98–148 RON", notes: "Per lane/hour" },
                { item: "Billiards — TrickShot (weekday morning)", price: "38 RON (~€7.60)", notes: "Mon–Fri 10:00–13:00" },
              ]} />

              <h3 id="cinema" className="text-stone-800 mt-8 mb-3" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.15rem", fontWeight: 700 }}>
                Cinema — Cinema City AFI Brașov
              </h3>
              <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                The Cinema City at AFI Brașov mall (Bulevardul 15 Noiembrie 78) has eight screens with a total capacity of over 1,000 seats. It shows the full range of international releases, usually in original language with Romanian subtitles. 3D screenings and premium formats (IMAX equivalent) are available. Tickets are cheaper on weekday mornings and through the Cinema City app.
              </p>
              <PriceTable rows={[
                { item: "Standard 2D ticket (adult)", price: "28–36 RON (~€5.60–7.20)", notes: "Weekday vs. weekend pricing" },
                { item: "3D ticket (adult)", price: "36–44 RON (~€7.20–8.80)", notes: "Includes 3D glasses (6 RON extra if you need them)" },
                { item: "Premium format (4DX / IMAX)", price: "52–64 RON (~€10.40–12.80)", notes: "Best experience in the region" },
              ]} />

              <h3 id="filarmonica-opera" className="text-stone-800 mt-8 mb-3" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.15rem", fontWeight: 700 }}>
                Culture: Filarmonica & Opera Brașov
              </h3>
              <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                Brașov has a genuine cultural infrastructure that punches above its size. The <strong>Filarmonica Brașov</strong> (Str. Hirscher 10) runs a full season of orchestral concerts, chamber music, and special events. The <strong>Opera Brașov</strong> (Str. Bisericii Române 51) stages opera, operetta, and ballet productions — often at prices that would be considered subsidised anywhere in Western Europe. Both institutions sell tickets online through bilet.ro.
              </p>
              <PriceTable rows={[
                { item: "Filarmonica — standard concert ticket", price: "40–80 RON (~€8–16)", notes: "Varies by programme and seat category" },
                { item: "Filarmonica — premium / gala concert", price: "80–160 RON (~€16–32)", notes: "Special events, international soloists" },
                { item: "Opera Brașov — opera / ballet ticket", price: "48–120 RON (~€9.60–24)", notes: "Varies by production and seat tier" },
                { item: "Weavers' Bastion — cultural event", price: "24–40 RON (~€4.80–8)", notes: "Concerts, exhibitions in medieval setting" },
              ]} />

              <InArticleBanner />

              {/* ===== NIGHTLIFE ===== */}
              <SectionLabel icon={<Moon size={16} />} label="Nightlife: Brașov after dark" color="oklch(0.28 0.08 260)" />

              <p className="text-stone-600 font-light leading-relaxed mb-6" style={{ lineHeight: "1.8" }}>
                Brașov's nightlife is concentrated in a compact area around <strong>Strada Republicii</strong> and the streets radiating from <strong>Piața Sfatului</strong>. It is not a city that stays up until 6am like Bucharest — the rhythm here is more civilised: dinner at 20:00, drinks from 22:00, dancing from midnight, and most venues winding down by 03:00–04:00. The upside is that the scene feels genuinely social rather than transactional, and the prices are some of the best in Romania.
              </p>

              <h3 id="craft-beer" className="text-stone-800 mt-6 mb-3" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.15rem", fontWeight: 700 }}>
                Craft beer & pub culture
              </h3>
              <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                Brașov has a well-developed craft beer scene, anchored by a handful of bars that take their taps seriously. <strong>Mustața</strong> (near <a href="/black-church-brasov-visitor-guide" className="text-amber-800 underline underline-offset-2 hover:text-amber-600">the Black Church</a>) is the local favourite — 400ml of a good local craft beer for around 20 RON, a terrace that fills up on warm evenings, and a crowd that is genuinely mixed between locals and expats. <strong>Merlin's Pub</strong> in the Old Town is the quieter, more atmospheric option — stone walls, low lighting, and a selection of imported beers. <strong>Motor Pub</strong> on Str. Mureșenilor has a rock-and-roll aesthetic and a loyal following among the 30+ crowd.
              </p>
              <PriceTable rows={[
                { item: "Draft beer (400 ml, local craft)", price: "20–25 RON (~€4–5)", notes: "Mustața, Motor Pub, most local bars" },
                { item: "Draft beer (500 ml, standard lager)", price: "18–22 RON (~€3.60–4.40)", notes: "Ursus, Timișoreana, Bergenbier" },
                { item: "Imported bottled beer (330 ml)", price: "22–30 RON (~€4.40–6)", notes: "Heineken, Peroni, Guinness" },
                { item: "Glass of house wine (150 ml)", price: "18–25 RON (~€3.60–5)", notes: "Romanian varieties; good quality" },
                { item: "Glass of quality wine (150 ml)", price: "30–50 RON (~€6–10)", notes: "Cramele Recaș, Davino, Serve" },
                { item: "Soft drink / water", price: "10–15 RON (~€2–3)", notes: "Standard bar pricing" },
              ]} />

              <h3 id="cocktail-bars" className="text-stone-800 mt-8 mb-3" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.15rem", fontWeight: 700 }}>
                Cocktail bars
              </h3>
              <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                The cocktail scene in Brașov has matured significantly in the last three years. <strong>Nomad Skybar</strong> (Str. Republicii 62, rooftop) is the most visually impressive option — an open-air rooftop terrace with views over the Old Town, a DJ from Thursday to Saturday, and a cocktail menu that takes inspiration from global street food cultures. <strong>Musik Cafe</strong> (Str. Republicii 42) is the more relaxed alternative: a courtyard bar with a vintage aesthetic, good music at a conversational volume, and cocktails that are consistently well-made. <strong>Galeria Civico</strong> (Str. Mureșenilor) is the art-world option — gallery space by day, cocktail bar by night.
              </p>
              <PriceTable rows={[
                { item: "Classic cocktail (Mojito, Aperol Spritz, G&T)", price: "28–36 RON (~€5.60–7.20)", notes: "Musik Cafe, most mid-range bars" },
                { item: "Signature cocktail (Nomad Skybar)", price: "40–55 RON (~€8–11)", notes: "Rooftop terrace; DJ Thu–Sat" },
                { item: "Espresso martini / premium cocktail", price: "36–48 RON (~€7.20–9.60)", notes: "Higher-end venues" },
                { item: "Shots (standard)", price: "12–16 RON (~€2.40–3.20)", notes: "Per shot; varies by spirit" },
                { item: "Bottle service (vodka/gin, 0.7L)", price: "320–480 RON (~€64–96)", notes: "Clubs; includes mixers" },
              ]} />

              <h3 id="clubs-dancing" className="text-stone-800 mt-8 mb-3" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.15rem", fontWeight: 700 }}>
                Clubs & dancing
              </h3>
              <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                Brașov's club scene is small but functional. <strong>Times</strong> (Str. Republicii 59, Clădirea Modarom) is the city's longest-running club — open since 2011, consistently popular, and the default choice for a proper night out. It plays a mix of commercial house, R&B, and Romanian pop, and gets genuinely busy from midnight on Friday and Saturday. <strong>Move Club</strong> is the alternative for those who prefer harder electronic music. <strong>Mood Club & Lounge</strong> is the more upscale option, with a dress code and a VIP area. Most clubs do not charge a cover on weeknights; weekend cover charges are modest by any European standard.
              </p>
              <PriceTable rows={[
                { item: "Club entry — weeknight (most venues)", price: "Free", notes: "No cover charge Monday–Thursday" },
                { item: "Club entry — weekend (Times, Move Club)", price: "25–40 RON (~€5–8)", notes: "Fri–Sat from 23:00" },
                { item: "Club entry — special events / DJs", price: "40–80 RON (~€8–16)", notes: "Guest DJs, themed nights" },
                { item: "Beer inside club (400 ml)", price: "20–28 RON (~€4–5.60)", notes: "Slightly higher than bar prices" },
                { item: "Cocktail inside club", price: "36–52 RON (~€7.20–10.40)", notes: "Standard club markup" },
              ]} />

              <h3 id="live-music" className="text-stone-800 mt-8 mb-3" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.15rem", fontWeight: 700 }}>
                Live music venues
              </h3>
              <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                Live music in Brașov happens in a few key spaces. <strong>Opus 9</strong> (Str. Mureșenilor 9) is the most reliable venue for jazz, blues, and acoustic sets — intimate, well-programmed, and worth checking for their monthly schedule. The <strong>Weavers' Bastion</strong> hosts outdoor concerts in summer, often with Romanian folk and classical crossover acts. The <strong>Filarmonica</strong> occasionally programmes lighter evening concerts in its foyer — the "Seri muzicale în foaier" series — at reduced ticket prices.
              </p>
              <PriceTable rows={[
                { item: "Opus 9 — live music evening (entry)", price: "24–40 RON (~€4.80–8)", notes: "Varies by artist; often includes first drink" },
                { item: "Weavers' Bastion — summer concert", price: "32–64 RON (~€6.40–12.80)", notes: "Outdoor; varies by programme" },
                { item: "Filarmonica foyer concert (Seri muzicale)", price: "24–40 RON (~€4.80–8)", notes: "Informal series, lower prices" },
              ]} />

              {/* Budget summary */}
              <div id="cost-scenarios" className="my-10 p-6 border border-stone-200" style={{ backgroundColor: "oklch(0.97 0.005 75)" }}>
                <h3 className="text-stone-900 mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.2rem", fontWeight: 700 }}>
                  What does a night out in Brașov actually cost?
                </h3>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  To give you a concrete reference point, here are three realistic evening scenarios:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[
                    {
                      label: "Casual evening",
                      items: ["Dinner at a mid-range restaurant: 80–120 RON", "2–3 beers at a pub: 50–75 RON", "Taxi home (Bolt): 20–30 RON"],
                      total: "150–225 RON (~€30–45)",
                    },
                    {
                      label: "Cocktail bar night",
                      items: ["Dinner: 100–150 RON", "3–4 cocktails at Nomad/Musik Cafe: 120–200 RON", "Taxi home: 20–30 RON"],
                      total: "240–380 RON (~€48–76)",
                    },
                    {
                      label: "Full club night",
                      items: ["Dinner: 100–150 RON", "Pre-drinks (2 cocktails): 70–100 RON", "Club entry: 25–40 RON", "4 drinks inside: 100–150 RON", "Taxi home: 25–35 RON"],
                      total: "320–475 RON (~€64–95)",
                    },
                  ].map((scenario, i) => (
                    <div key={i} className="bg-white border border-stone-200 p-4">
                      <p className="text-xs uppercase tracking-widest font-semibold mb-3" style={{ color: "oklch(0.38 0.12 15)" }}>{scenario.label}</p>
                      <ul className="space-y-1 mb-4">
                        {scenario.items.map((item, j) => (
                          <li key={j} className="text-xs text-stone-500 font-light leading-relaxed">{item}</li>
                        ))}
                      </ul>
                      <div className="pt-3 border-t border-stone-100">
                        <p className="text-sm font-bold text-stone-900" style={{ fontFamily: "Playfair Display, serif" }}>{scenario.total}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <h2 id="seasonal-highlights" className="text-stone-900 mt-10 mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.5rem", fontWeight: 700 }}>
                Seasonal highlights: when Brașov is at its best
              </h2>
              <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                Brașov's entertainment calendar has a clear seasonal rhythm. <strong>Summer (June–September)</strong> is when the outdoor terraces are packed, Nomad Skybar runs its full programme, and the Weavers' Bastion hosts open-air concerts. The <strong>Golden Stag Festival</strong> (Cerbul de Aur), held in Piața Sfatului every few years, is one of Romania's most celebrated music events. <strong>Winter (November–February)</strong> brings the Christmas Market — one of the best in Eastern Europe — and a shift to cosy indoor venues, mulled wine, and the ski season at Poiana Brașov (30 minutes away). <strong>Spring and autumn</strong> are the quietest seasons, with lower prices and more availability at popular venues.
              </p>

              <h2 id="practical-tips" className="text-stone-900 mt-10 mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.5rem", fontWeight: 700 }}>
                Practical tips for residents and long-term visitors
              </h2>
              <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                A few things that will save you money and improve your experience. First, <strong>Bolt is the dominant ride-hailing app</strong> in Brașov — always cheaper than a street taxi, and reliable. A ride across the city centre costs 15–25 RON. Second, most venues in the Old Town have a <strong>terrace premium</strong>: the same beer costs 2–4 RON more if you are sitting outside on Piața Sfatului versus inside. Third, <strong>weeknight pricing</strong> is significantly lower at clubs and some bars — if you are flexible, Thursday is often the best night for a night out. Finally, many cultural venues (Filarmonica, Opera) offer <strong>student and senior discounts</strong> of 25–50% — always worth asking.
              </p>

              <div className="my-8 px-5 py-4 border-l-4" style={{ borderLeftColor: "oklch(0.55 0.14 60)", backgroundColor: "oklch(0.97 0.005 75)" }}>
                <p className="text-stone-700 text-sm font-light leading-relaxed">
                  <strong className="text-stone-900">Bottom line:</strong> Brașov offers a genuinely rich entertainment landscape for a city of its size. A comfortable evening out — dinner, drinks, and a taxi home — costs between €30 and €75 per person. A full club night stays comfortably under €100. Compared to Warsaw, Prague, or Budapest (let alone Western European capitals), the value is exceptional — and it fits into a broader picture of <Link href="/cost-of-living-brasov" className="underline decoration-dotted underline-offset-2 hover:text-stone-900 transition-colors" style={{ color: "oklch(0.38 0.12 15)" }}>Brașov's cost of living in 2026</Link> that remains competitive for a European city of this quality. The city rewards those who engage with it beyond the tourist circuit: the best bars, the best concerts, and the best evenings out are the ones that locals know about, not the ones on TripAdvisor's front page.
                </p>
              </div>

              {/* Related articles */}
              <div className="mt-12 pt-8 border-t border-stone-200">
                <h3 className="text-stone-900 mb-6 text-sm uppercase tracking-widest font-semibold">Related articles</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                  {[
                    {
                      href: "/cost-of-living-brasov",
                      category: "Relocation",
                      title: "Cost of Living in Brașov 2026: the honest numbers",
                      img: "https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/article4-market-hero_bed3bdbc.webp",
                    },
                    {
                      href: "/why-expats-choose-brasov",
                      category: "City Guide",
                      title: "Brașov, straight up: beyond the bear photos and the mountain air clichés",
                      img: "https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/hero-article1-v1-mBURY4muT8nNAjUnDwQRbz.webp",
                    },
                    {
                      href: "/cartier-transilvania-brasov",
                      category: "Real Estate",
                      title: "Buying an Apartment in Brașov in 2026: What to Look For, and One Development Worth Knowing",
                      img: "https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/XbbLvxSueOHmjHcF.webp",
                    },
                  ].map((article, i) => (
                    <Link key={i} href={article.href}>
                      <div className="group border border-stone-200 overflow-hidden hover:border-stone-400 transition-colors cursor-pointer">
                        <div className="overflow-hidden" style={{ height: "160px" }}>
                          <img
                            src={article.img}
                            alt={article.title}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                            loading="lazy"
                          />
                        </div>
                        <div className="p-4">
                          <p className="text-xs uppercase tracking-widest font-semibold mb-2" style={{ color: "oklch(0.38 0.12 15)" }}>
                            {article.category}
                          </p>
                          <p className="text-stone-800 font-semibold leading-snug group-hover:text-stone-600 transition-colors" style={{ fontFamily: "Playfair Display, serif", fontSize: "0.95rem" }}>
                            {article.title}
                          </p>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
                <div className="mt-6 text-center">
                  <Link href="/blog">
                    <button className="text-xs uppercase tracking-widest font-semibold px-6 py-3 border border-stone-300 text-stone-600 hover:border-stone-800 hover:text-stone-900 transition-colors">
                      View all articles →
                    </button>
                  </Link>
                </div>
              </div>
            </div>
          </main>

          {/* Sidebar */}
          <aside className="w-full lg:w-72 flex-shrink-0">
            <div className="sticky top-24 space-y-8">
            <SidebarAd />

            {/* Table of contents */}
            <div className="border border-stone-200 p-5">
              <p className="text-xs uppercase tracking-widest font-semibold text-stone-500 mb-4">In this article</p>
              <nav className="space-y-2">
                {[
                  { label: "Telecabina Tâmpa", anchor: "telecabina-tampa" },
                  { label: "Escape rooms", anchor: "escape-rooms" },
                  { label: "Parc Aventura", anchor: "parc-aventura" },
                  { label: "Paradisul Acvatic", anchor: "paradisul-acvatic" },
                  { label: "Bowling & billiards", anchor: "bowling-billiards" },
                  { label: "Cinema", anchor: "cinema" },
                  { label: "Filarmonica & Opera", anchor: "filarmonica-opera" },
                  { label: "Craft beer & pubs", anchor: "craft-beer" },
                  { label: "Cocktail bars", anchor: "cocktail-bars" },
                  { label: "Clubs & dancing", anchor: "clubs-dancing" },
                  { label: "Live music", anchor: "live-music" },
                  { label: "Cost scenarios", anchor: "cost-scenarios" },
                  { label: "Seasonal highlights", anchor: "seasonal-highlights" },
                  { label: "Practical tips", anchor: "practical-tips" },
                ].map((item, i) => (
                  <a
                    key={i}
                    href={`#${item.anchor}`}
                    className="block text-sm text-stone-500 font-light hover:text-stone-800 transition-colors leading-snug no-underline"
                    onClick={(e) => {
                      e.preventDefault();
                      const el = document.getElementById(item.anchor);
                      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                    }}
                  >
                    {item.label}
                  </a>
                ))}
              </nav>
            </div>

            {/* Quick facts */}
            <div className="border border-stone-200 p-5">
              <p className="text-xs uppercase tracking-widest font-semibold text-stone-500 mb-4">Quick facts</p>
              <div className="space-y-3">
                {[
                  { label: "Beer at a pub", value: "20–25 RON" },
                  { label: "Cocktail (mid-range)", value: "35–45 RON" },
                  { label: "Club entry (weekend)", value: "25–40 RON" },
                  { label: "Cinema ticket", value: "28–36 RON" },
                  { label: "Escape room (4 pax)", value: "320 RON total" },
                  { label: "Cable car (return)", value: "45 RON" },
                  { label: "Opera ticket", value: "48–120 RON" },
                  { label: "Night out (budget)", value: "~150–225 RON" },
                ].map((fact, i) => (
                  <div key={i} className="flex justify-between items-baseline">
                    <span className="text-xs text-stone-500 font-light">{fact.label}</span>
                    <span className="text-xs font-mono font-semibold text-stone-800">{fact.value}</span>
                  </div>
                ))}
              </div>
            </div>
              {/* Newsletter */}
              <div className="border border-stone-200 p-5">
                <h4 className="font-bold text-stone-900 mb-2 text-sm" style={{ fontFamily: "Playfair Display, serif" }}>Stay in the loop</h4>
                <p className="text-stone-500 text-xs font-light leading-relaxed mb-3">No spam. One email per week with what is actually happening in Brașov.</p>
                <NewsletterInlineLight />
              </div>
              {/* CTA card — Relocation support */}
              <div className="p-5 text-white" style={{ backgroundColor: "oklch(0.38 0.12 15)" }}>
                <p className="text-xs uppercase tracking-widest mb-3 opacity-70">Relocation support</p>
                <h4 className="font-bold mb-3 leading-snug" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.1rem" }}>
                  Moving to Brașov?
                </h4>
                <p className="text-sm font-light leading-relaxed mb-4 opacity-80">
                  We help expats find apartments, navigate bureaucracy, and settle in — without the stress.
                </p>
                <Link href="/contact">
                  <button className="w-full text-xs py-2.5 border border-white/40 hover:bg-white/10 transition-colors tracking-widest">
                    BOOK A FREE CALL
                  </button>
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <SiteFooter />
    </div>
    </>
  );
}
